import type { RouteLocationNormalized } from 'vue-router'
import { CITIES, CITIES_RECORD, DEFAULT_CITY } from '~/composables/city'
import { getRegistrationPaymentEnabledCityIds } from '~/composables/registration-payment-cities'
import { getSubscriptionEnabledCityIds } from '~/composables/subscription-cities'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '~/composables/translate/translations'

export default defineNuxtRouteMiddleware((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  // Redirect webview select page to onboarding page (app entry point migration)
  if (to.path.endsWith('/service/select') || to.path.endsWith('/service/select/')) {
    return navigateTo({
      path: to.path.replace('/service/select', '/service/onboarding').replace(/\/$/, ''),
      query: to.query,
    }, { replace: true })
  }

  // Guard: redirect to onboarding if trying to access subscription funnel in non-subscription-enabled city
  if (to.path.includes('/subscription/') || to.path.endsWith('/service/select-funnel')) {
    const enabledIds = getSubscriptionEnabledCityIds()
    const cityParam = to.params._city?.toString()?.toLowerCase() ?? DEFAULT_CITY
    const city = CITIES_RECORD[cityParam]
    if (!city || !enabledIds.includes(city.locationId)) {
      const lang = to.params._lang?.toString() ?? DEFAULT_LOCALE
      return navigateTo(`/webview/${lang}/${cityParam}/service/onboarding`, { replace: true })
    }
  }

  // Guard: redirect to personal page if trying to access registration/payment sub-routes in non-enabled city
  if (to.path.includes('/personal/details') || to.path.includes('/personal/payment') || to.path.includes('/personal/auth-callback')) {
    const enabledIds = getRegistrationPaymentEnabledCityIds()
    const cityParam = to.params._city?.toString()?.toLowerCase() ?? DEFAULT_CITY
    const city = CITIES_RECORD[cityParam]
    if (!city || !enabledIds.includes(city.locationId)) {
      const lang = to.params._lang?.toString() ?? DEFAULT_LOCALE
      return navigateTo(`/${lang}/${cityParam}/personal`, { replace: true })
    }
  }

  const pathParams = from.path.match(/\/(\w+)\/([^/]+)\/?/) || []

  if (to.matched.length === 0) {
    const redirectUrl = `/${getLanguage(pathParams)}/${getCity(pathParams)}/category/`

    return navigateTo({
      path: redirectUrl,
      // keep query parameters on redirect to category component
      query: to.query,
    })
  }
})

const LANGUAGES_ALLOWED: Set<string> = new Set(SUPPORTED_LOCALES.map(l => l.toString()))
const CITIES_ALLOWED: Set<string> = new Set(CITIES.map(c => c.id))

function getCity(pathParams: any[]): string {
  const city: string = pathParams[2] || DEFAULT_CITY
  return CITIES_ALLOWED.has(city) ? city : DEFAULT_CITY
}

function getLanguage(pathParams: any[]): string {
  const lang: string = pathParams[1] || DEFAULT_LOCALE
  return LANGUAGES_ALLOWED.has(lang) ? lang : DEFAULT_LOCALE
}
